// Administrador Master data (ADR-0047). Every number comes from the server (database functions and the
// ad server's billable ledger); the browser only renders it. Operator role and MFA are re-checked by
// the API and the database on every call; a 403 STEP_UP_REQUIRED opens the step-up dialog.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, call } from "@/api/client";

export const adminKeys = {
  all: ["admin"] as const,
  path: (p: string, q?: Record<string, string | undefined>) => ["admin", p, q ?? {}] as const,
};

type AdminPath =
  | "/admin/dashboard"
  | "/admin/companies"
  | "/admin/purchase-orders"
  | "/admin/authorizations"
  | "/admin/credit"
  | "/admin/credit-control"
  | "/admin/billing/summary"
  | "/admin/invoices"
  | "/admin/consumption"
  | "/admin/settings/notifications"
  | "/admin/cpm-rates";

export function useAdmin<T>(
  path: AdminPath,
  query: Record<string, string | undefined> = {},
  /** Ask again every 1.5 s while this says a measurement is still advancing (report 106). */
  stillMeasuring?: (data: T) => boolean
) {
  return useQuery({
    queryKey: adminKeys.path(path, query),
    queryFn: async () => {
      const clean = Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== ""));
      const r = await call(() => api.GET(path, { params: { query: clean as never } }));
      return r.data as unknown as T;
    },
    ...(stillMeasuring
      ? {
          refetchInterval: (q: { state: { data: T | undefined } }) =>
            q.state.data !== undefined && stillMeasuring(q.state.data) ? 1500 : false,
        }
      : {}),
  });
}

/** A measurement advances one slice per call (report 106): call again until it finishes. Bounded, never forever. */
async function untilFinished<T>(step: () => Promise<T>, finished: (r: T) => boolean): Promise<T> {
  let r = await step();
  for (let i = 0; i < 2000 && !finished(r); i++) r = await step();
  return r;
}

export function useCompanyOverview<T>(orgId: string) {
  return useQuery({
    queryKey: adminKeys.path("/admin/companies/overview", { orgId }),
    queryFn: async () => {
      const r = await call(() => api.GET("/admin/companies/{orgId}/overview", { params: { path: { orgId } } }));
      return r.data as unknown as T;
    },
  });
}

/** Any admin mutation; on success every admin query is refreshed (counts, queues, credit). */
export function useAdminMutation<I>(run: (input: I) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.all }),
  });
}

export const adminPost = {
  status: (
    orgId: string,
    body: { status: "ACTIVE" | "SUSPENDED" | "REJECTED"; reason: string | null; acknowledgeDuplicate?: boolean }
  ) => call(() => api.POST("/admin/companies/{orgId}/status", { params: { path: { orgId } }, body })),
  patch: (orgId: string, body: Record<string, unknown>) =>
    call(() => api.PATCH("/admin/companies/{orgId}", { params: { path: { orgId } }, body: body as never })),
  terms: (
    orgId: string,
    body: {
      paymentMode: "PREPAID" | "PURCHASE_ORDER" | "CREDIT";
      purchaseOrdersEnabled: boolean;
      creditLimitUsd: string;
      paymentTermsDays: number;
      notes: string | null;
    }
  ) => call(() => api.PUT("/admin/companies/{orgId}/terms", { params: { path: { orgId } }, body })),
  poDecision: (
    purchaseOrderId: string,
    body: { decision: "APPROVE" | "REJECT"; approvedUsd: string | null; reason: string | null }
  ) =>
    call(() =>
      api.POST("/admin/purchase-orders/{purchaseOrderId}/decision", { params: { path: { purchaseOrderId } }, body })
    ),
  authDecision: (
    authorizationId: string,
    body: { decision: "APPROVE" | "REJECT"; approvedUsd: string | null; reason: string | null }
  ) =>
    call(() =>
      api.POST("/admin/authorizations/{authorizationId}/decision", { params: { path: { authorizationId } }, body })
    ),
  budgetCheck: (authorizationId: string) =>
    untilFinished(
      () =>
        call(() =>
          api.POST("/admin/authorizations/{authorizationId}/budget-check", { params: { path: { authorizationId } } })
        ),
      (r) => (r as { outcome?: string }).outcome !== "IN_PROGRESS"
    ),
  recipients: (recipients: Array<{ email: string; events: string[] }>) =>
    call(() => api.PUT("/admin/settings/notifications", { body: { recipients: recipients as never } })),
  retryNotification: (notificationId: string) =>
    call(() =>
      api.POST("/admin/settings/notifications/{notificationId}/retry", { params: { path: { notificationId } } })
    ),
  invoice: (
    orgId: string,
    body: {
      invoiceNumber: string;
      issuedOn: string;
      dueOn: string;
      periodStart: string;
      periodEnd: string;
      notes: string | null;
      idempotencyKey: string;
      lines: Array<{ authorizationId: string; description: string; impressions: number | null; amountUsd: string }>;
    }
  ) => call(() => api.POST("/admin/companies/{orgId}/invoices", { params: { path: { orgId } }, body })),
  voidInvoice: (invoiceId: string, reason: string) =>
    call(() => api.POST("/admin/invoices/{invoiceId}/void", { params: { path: { invoiceId } }, body: { reason } })),
  payment: (
    invoiceId: string,
    body: {
      amountUsd: string;
      receivedOn: string;
      method: "TRANSFER" | "CHECK" | "CASH" | "CARD" | "OTHER";
      reference: string;
      idempotencyKey: string;
    }
  ) => call(() => api.POST("/admin/invoices/{invoiceId}/payments", { params: { path: { invoiceId } }, body })),
  voidPayment: (paymentId: string, reason: string) =>
    call(() => api.POST("/admin/payments/{paymentId}/void", { params: { path: { paymentId } }, body: { reason } })),
  creditControlRun: () =>
    untilFinished(
      () => call(() => api.POST("/admin/credit-control/run")),
      (r) => (r as { status?: string }).status !== "RUNNING"
    ),
  creditPolicy: (body: {
    orgSafetyMarginBps: number;
    campaignSafetyMarginBps: number;
    warningThresholdBps: number;
    consumptionMaxAgeMinutes: number;
    reason: string;
  }) => call(() => api.PUT("/admin/credit-control/policy", { body })),
  releaseHold: (holdId: string, reason: string) =>
    call(() =>
      api.POST("/admin/credit-control/holds/{holdId}/release", { params: { path: { holdId } }, body: { reason } })
    ),
  closePo: (purchaseOrderId: string, reason: string) =>
    call(() =>
      api.POST("/admin/purchase-orders/{purchaseOrderId}/close", {
        params: { path: { purchaseOrderId } },
        body: { reason },
      })
    ),
  cpm: (body: {
    organizationId: string | null;
    product: "RICH_MEDIA_STANDARD" | "VIDEO_RICH_MEDIA" | "ADVERGAMING" | "SURVEY_RICH_MEDIA";
    cpmUsd: string;
    effectiveFrom: string;
    reason: string;
  }) => call(() => api.POST("/admin/cpm-rates", { body })),
};
